import React from 'react';
import TeachEnglishPageLayout from '../components/TeachEnglishPageLayout';

const WatchAndDiscuss = () => {
  const title = 'Watch and Discuss';
  const image = '/Images/3people1a.webp';
  const intro = 'Video is the most engaging input source—but only if tasks make viewing active. This guide covers video selection criteria, pre/while/post frameworks, discussion protocols, and a curated library of classroom-ready clips.';

  const seoContent = [
    'Not all videos are equal for language learning. Ideal classroom clips: 2–5 minutes (attention span), clear audio, visual support for meaning, authentic but not opaque (graded or with scaffolding), and a clear communicative purpose (narrative, explanation, opinion, demonstration). Avoid: heavy slang, strong accents without support, culturally opaque humor, pure entertainment without language focus.',
    'Follow the video lesson arc: Pre-viewing (activate topic vocab, predict content from title/screenshot, set a gist question) → While-viewing (multiple viewings with escalating tasks: View 1 = gist/order events; View 2 = detail/comprehension questions; View 3 = language focus—target structures, discourse markers, pronunciation) → Post-viewing (discussion, role-play, writing, vocabulary consolidation, strategy reflection).',
    'Discussion protocols that ensure participation: 1) Think-Pair-Share (individual think time → pair discussion → whole-class share). 2) Conversation Stations (4–5 question posters, groups rotate every 3 mins). 3) Fishbowl (inner circle discusses, outer circle observes with checklist, then swap). 4) Silent Discussion (write responses on shared doc/padlet, then comment on peers\' ideas). 5) Debate Corners (agree/disagree/depends—move, then justify).',
    'Language focus during viewing: pause at target items. \"Did you hear \'actually\'? What function?\" \"Notice the intonation on \'really?\'\" \"That\'s a zero conditional—why?\" Create a \"Video Language Log\" template: Time stamp | Language chunk | Function/Meaning | My example sentence. Students complete 3–5 entries per clip.',
    'Curate a sustainable video library. Tag each clip: CEFR level, topic, grammar focus, vocabulary set, discourse type, length, source URL, license (Creative Commons, YouTube Standard, school-licensed). Share via a class playlist (YouTube Unlisted, Wakelet, Padlet) so students can re-watch. Build a \"Video of the Week\" routine—5 minutes every Monday.',
  ];

  const levels = [
    {
      level: 'frameworks',
      label: 'Video Lesson Frameworks',
      tagline: 'Structures for any clip',
      cards: [
        {
          title: '3-View Lesson Planner',
          image: '/Images/grammar1.webp',
          desc: 'Template: View 1 (gist), View 2 (detail), View 3 (language) with task boxes and timing.',
          links: [{ text: 'Download', to: '/resources/3-view-planner.pdf' }],
        },
        {
          title: 'Discussion Protocol Cards',
          image: '/Images/2people1.webp',
          desc: 'Laminated quick-start cards for 10 protocols: Think-Pair-Share, Fishbowl, Silent, Debate, etc.',
          links: [{ text: 'Download', to: '/resources/discussion-protocols.pdf' }],
        },
        {
          title: 'Video Language Log Template',
          image: '/Images/vocabulary.webp',
          desc: 'Student worksheet: timestamp, chunk, function, example, translation—builds noticing habit.',
          links: [{ text: 'Download', to: '/resources/video-language-log.pdf' }],
        },
      ],
    },
    {
      level: 'library',
      label: 'Curated Video Library',
      tagline: 'Tagged, leveled, ready-to-teach',
      cards: [
        {
          title: 'TED-Ed & TED Talks (B1–C1)',
          image: '/Images/online-teacher.webp',
          desc: '40+ talks with lesson packs: comprehension, vocab, discussion, project ideas. CC licensed.',
          links: [{ text: 'Browse Library', to: '/videos/ted-ed' }],
        },
        {
          title: 'News & Current Events (B1–B2)',
          image: '/Images/tutor2-1280x470.webp',
          desc: 'Weekly 3-min clips from BBC Learning English, VOA, News in Levels—with transcripts & quizzes.',
          links: [{ text: 'View This Week', to: '/videos/news' }],
        },
        {
          title: 'Short Films & Animations (A2–B2)',
          image: '/Images/3people1a.webp',
          desc: 'Wordless films for narrative tenses, Pixar shorts for prediction, adverts for pragmatic analysis.',
          links: [{ text: 'Browse Films', to: '/videos/short-films' }],
        },
        {
          title: 'Authentic Interactions (B2–C1)',
          image: '/Images/speaking.webp',
          desc: 'Real conversations: job interviews, negotiations, small talk, academic seminars—with discourse analysis.',
          links: [{ text: 'Browse Clips', to: '/videos/authentic-interactions' }],
        },
      ],
    },
  ];

  return (
    <TeachEnglishPageLayout
      title={title}
      image={image}
      intro={intro}
      seoContent={seoContent}
      levels={levels}
      seoTitle="Watch and Discuss: Video Lesson Frameworks, Discussion Protocols & Curated Library"
      seoDescription="Guide to teaching with video in ESL. Selection criteria, 3-view lesson arc, 5 discussion protocols (Think-Pair-Share, Fishbowl, etc.), language noticing logs, and free curated library (TED-Ed, news, short films, authentic interactions)."
    />
  );
};

export default WatchAndDiscuss;